<script setup name="TvbHomeNotification">
import { reactive, onMounted, ref, nextTick, computed, inject, watch } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination } from 'swiper/modules'
import { getInstantMessage, readInstantMessage } from '@/api/home'
import { isMobileDevice } from '@/utils'

const width = inject('width')
const swiperRef = ref()
const messagesRef = ref([])
const notification = reactive({
  activeIndex: 0,
  messages: [],
  swiper: null,
  ellipsis: true
})

const getMessages = async () => {
  const { data, code, message } = await getInstantMessage()
  if (code === 0) {
    notification.messages = data.list
  } else {
    console.error({ data, code, message })
  }
}

const changeSwiper = (event) => (notification.activeIndex = event.activeIndex)

const isMobile = computed(() => isMobileDevice() || width.value < 768)

const close = () => {
  const activeIndex = notification.activeIndex
  const swiper = swiperRef.value.$el.swiper
  swiper.slidePrev()
  notification.activeIndex && notification.activeIndex--
  notification.messages = notification.messages.filter((message, index) => {
    if (index === activeIndex) {
      readInstantMessage(message)
      return false
    } else return true
  })
  notification.activeIndex &&
    nextTick(() => {
      swiper.update()
      swiper.navigation.update()
      swiper.pagination.update()
    })
}

const offset = computed(() =>
  notification.messages.length === 1 ? 0 : isMobile.value ? 16 : width.value > 1440 ? 28 : 24
)

const marginLeft = computed(() =>
  notification.messages.length === 1
    ? isMobile.value
      ? '16px'
      : width.value > 1440
        ? '28px'
        : '24px'
    : null
)

const setBodyOverflow = (overflow) => {
  document.body.style.overflow = isMobile.value ? '' : overflow
}

onMounted(() => setTimeout(() => getMessages(), 1000))

watch(
  messagesRef,
  (messagesRef) => {
    messagesRef?.forEach((mel, i) => {
      const house = mel?.getBoundingClientRect()
      const room = mel?.querySelector('span')?.getBoundingClientRect()
      notification.messages[i].showUnfold = house.height < room.height
    })
  },
  { deep: true }
)
</script>

<template>
  <div
    class="tvb-home-notification"
    @mouseenter="() => setBodyOverflow('hidden')"
    @mouseleave="() => setBodyOverflow('')"
    :class="{
      show: notification.messages.length,
      ellipsis: notification.ellipsis && isMobile,
      mobile: isMobile
    }"
  >
    <div class="tvb-home-notification_container">
      <header class="tvb-home-notification_header">
        <div class="horn">
          <img class="horn-img" src="/image/img_message_horn.png" alt="horn" />
        </div>
        <h3 class="title">突發消息</h3>
        <span class="number" v-if="notification.messages.length > 1">
          {{ notification.messages.length }}
        </span>
        <div class="close" @click="close">
          <tvb-svg-icon class="normal" name="icon_message_close_normal" />
          <tvb-svg-icon class="hover" name="icon_message_close_hover" />
        </div>
      </header>
      <main class="tvb-home-notification_contnet">
        <swiper
          ref="swiperRef"
          class="tvb-home-notification_swiper"
          :modules="[Navigation, Pagination]"
          :space-between="offset"
          :slides-offset-after="offset"
          :slides-offset-before="offset"
          :slides-per-view="'auto'"
          @init="changeSwiper"
          @slide-change-transition-end="changeSwiper"
          :navigation="{
            nextEl: '.tvb-home-notification_swiper-button-next',
            prevEl: '.tvb-home-notification_swiper-button-prev'
          }"
          :pagination="{
            el: '.tvb-home-notification_swiper-pagination',
            clickable: true
          }"
        >
          <swiper-slide
            class="tvb-home-notification_swiper-slide"
            v-for="(message, index) in notification.messages"
            :style="{
              marginLeft,
              boxShadow: `0 0 ${isMobile ? '4px' : '8px'} 0 rgba(0, 0, 0, 0.0${
                notification.activeIndex === index ? 8 : 0
              })`
            }"
            :key="index"
          >
            <div class="content">
              <div class="house">
                <div class="message">
                  <p
                    class="message-text"
                    ref="messagesRef"
                    :class="{
                      ellipsis: notification.ellipsis && isMobile,
                      unfold: message.showUnfold
                    }"
                  >
                    <button
                      class="message-unfold"
                      v-if="isMobile && notification.ellipsis && message.showUnfold"
                      @click="notification.ellipsis = false"
                    >
                      查看詳情<tvb-svg-icon
                        class="message-unfold-arrow"
                        name="icon_list_arrow_blue"
                      />
                    </button>
                    <span>{{ message.content }}</span>
                  </p>
                </div>
              </div>
            </div>
          </swiper-slide>
          <div class="tvb-home-notification_swiper-button-prev">
            <tvb-svg-icon class="arrow normal" name="icon_list_arrow_grey_" />
            <tvb-svg-icon class="arrow hover" name="icon_list_arrow_blue" />
          </div>
          <div class="tvb-home-notification_swiper-button-next">
            <tvb-svg-icon class="arrow normal" name="icon_list_arrow_grey_" />
            <tvb-svg-icon class="arrow hover" name="icon_list_arrow_blue" />
          </div>
        </swiper>
      </main>
      <footer class="tvb-home-notification_footer" v-show="notification.messages.length > 1">
        <div class="tvb-home-notification_swiper-pagination" :class="{ mobile: isMobile }"></div>
      </footer>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tvb-home-notification {
  &:not(.mobile) {
    --tvb-home-notification-border-radius: 8px;
    --tvb-home-notification-box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.2);
    --tvb-home-notification-fixed-offset: 16px;
    --tvb-home-notification-fixed-bottom: -350px;
    --tvb-home-notification-max-width: 540px;
    --tvb-home-notification-width: 540px;
    --tvb-home-notification-height: 324px;
    --tvb-home-notification-header-padding: 0 16px 0 10px;
    --tvb-home-notification-header-horn-width: 80px;
    --tvb-home-notification-header-horn-height: 56px;
    --tvb-home-notification-header-horn-size: 80px;
    --tvb-home-notification-header-horn-margin-bottom: 2px;
    --tvb-home-notification-header-horn-top: -26px;
    --tvb-home-notification-header-title-font-size: 20px;
    --tvb-home-notification-header-title-letter-spacing: 0.5px;
    --tvb-home-notification-header-title-line-height: 28px;
    --tvb-home-notification-header-title-margin: 0 4px;
    --tvb-home-notification-header-number-size: 18px;
    --tvb-home-notification-header-number-font-size: 12px;
    --tvb-home-notification-header-close-size: 20px;
    --tvb-home-notification-header-close-box-size: 20px;
    --tvb-home-notification-fixed-box-offset: 16px;
    --tvb-home-notification-contnet-height: 232px;
    --tvb-home-notification-swiper-offset: 28px;
    --tvb-home-notification-swiper-button-size: 36px;
    --tvb-home-notification-swiper-button-arrow-size: 16px;
    --tvb-home-notification-swiper-button-offset: 10px;
    --tvb-home-notification-swiper-pagination-height: 36px;
    --tvb-home-notification-swiper-pagination-bullet-height: 8px;
    --tvb-home-notification-swiper-pagination-bullet-radius: 8px;
    --tvb-home-notification-swiper-pagination-bullet-width: 8px;
    --tvb-home-notification-swiper-pagination-bullet-margin: 0 0 0 8px;
    --tvb-home-notification-contnet-message-font-size: 18px;
    --tvb-home-notification-contnet-message-letter-spacing: 0.8px;
    --tvb-home-notification-contnet-message-line-height: 32px;
    --tvb-home-notification-contnet-message-padding: 20px;
    --tvb-home-notification-contnet-message-ellipsis-line: 99999999;
    --tvb-home-notification-contnet-message-text-overflow: inherit;
    --tvb-home-notification-house-scrollbar-width: 4px;
    --tvb-home-notification-house-scrollbar-padding: 6px;

    @media (max-width: 1440px) and (min-width: 768px) {
      --tvb-home-notification-border-radius: 8px;
      --tvb-home-notification-box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.2);
      --tvb-home-notification-fixed-offset: 16px;
      --tvb-home-notification-fixed-bottom: -350px;
      --tvb-home-notification-max-width: 480px;
      --tvb-home-notification-width: 480px;
      --tvb-home-notification-height: 266px;
      --tvb-home-notification-header-padding: 0 12px 0 8px;
      --tvb-home-notification-header-horn-width: 68px;
      --tvb-home-notification-header-horn-height: 48px;
      --tvb-home-notification-header-horn-size: 68px;
      --tvb-home-notification-header-horn-margin-bottom: 2px;
      --tvb-home-notification-header-horn-top: -22px;
      --tvb-home-notification-header-title-font-size: 18px;
      --tvb-home-notification-header-title-letter-spacing: 0.5px;
      --tvb-home-notification-header-title-line-height: 24px;
      --tvb-home-notification-header-title-margin: 0 4px;
      --tvb-home-notification-header-number-size: 18px;
      --tvb-home-notification-header-number-font-size: 12px;
      --tvb-home-notification-header-close-size: 18px;
      --tvb-home-notification-header-close-box-size: 18px;
      --tvb-home-notification-fixed-box-offset: 12px;
      --tvb-home-notification-contnet-height: 188px;
      --tvb-home-notification-swiper-offset: 24px;
      --tvb-home-notification-swiper-button-size: 32px;
      --tvb-home-notification-swiper-button-arrow-size: 14.22px;
      --tvb-home-notification-swiper-button-offset: 8px;
      --tvb-home-notification-swiper-pagination-height: 30px;
      --tvb-home-notification-swiper-pagination-bullet-height: 6px;
      --tvb-home-notification-swiper-pagination-bullet-radius: 6px;
      --tvb-home-notification-swiper-pagination-bullet-width: 6px;
      --tvb-home-notification-swiper-pagination-bullet-margin: 0 0 0 6px;
      --tvb-home-notification-contnet-message-font-size: 16px;
      --tvb-home-notification-contnet-message-letter-spacing: 0.8px;
      --tvb-home-notification-contnet-message-line-height: 26px;
      --tvb-home-notification-contnet-message-padding: 16px;
      --tvb-home-notification-house-scrollbar-width: 4px;
      --tvb-home-notification-house-scrollbar-padding: 6px;
    }
  }

  &.mobile {
    --tvb-home-notification-border-radius: 6px;
    --tvb-home-notification-box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.25);
    --tvb-home-notification-fixed-offset: 12px;
    --tvb-home-notification-fixed-bottom: -350px;
    --tvb-home-notification-max-width: 400px;
    --tvb-home-notification-width: calc(100vw - var(--tvb-home-notification-fixed-offset) * 2);
    --tvb-home-notification-height: 319px;
    --tvb-home-notification-header-padding: 0 12px 0 4px;
    --tvb-home-notification-header-horn-width: 56px;
    --tvb-home-notification-header-horn-height: 42px;
    --tvb-home-notification-header-horn-size: 56px;
    --tvb-home-notification-header-horn-margin-bottom: 2px;
    --tvb-home-notification-header-horn-top: -16px;
    --tvb-home-notification-header-title-font-size: 16px;
    --tvb-home-notification-header-title-letter-spacing: 0.5px;
    --tvb-home-notification-header-title-line-height: 22px;
    --tvb-home-notification-header-title-margin: 0 4px 0 2px;
    --tvb-home-notification-header-number-size: 16px;
    --tvb-home-notification-header-number-font-size: 10px;
    --tvb-home-notification-header-close-size: 16px;
    --tvb-home-notification-header-close-box-size: 32px;
    --tvb-home-notification-fixed-box-offset: 4px;
    --tvb-home-notification-contnet-height: 258px;
    --tvb-home-notification-swiper-offset: 16px;
    --tvb-home-notification-swiper-button-size: 0;
    --tvb-home-notification-swiper-button-arrow-size: 0;
    --tvb-home-notification-swiper-button-offset: 0;
    --tvb-home-notification-swiper-pagination-height: 19px;
    --tvb-home-notification-swiper-pagination-bullet-height: 3px;
    --tvb-home-notification-swiper-pagination-bullet-radius: 2px;
    --tvb-home-notification-swiper-pagination-bullet-width: 8px;
    --tvb-home-notification-swiper-pagination-bullet-margin: 0 0 0 4px;
    --tvb-home-notification-contnet-message-font-size: 16px;
    --tvb-home-notification-contnet-message-letter-spacing: 0.8px;
    --tvb-home-notification-contnet-message-line-height: 26px;
    --tvb-home-notification-contnet-message-padding: 12px;
    --tvb-home-notification-contnet-message-before-height: 0px;
    --tvb-home-notification-contnet-message-ellipsis-display: none;
    --tvb-home-notification-contnet-message-ellipsis-line: 99999999;
    --tvb-home-notification-contnet-message-text-overflow: inherit;
    --tvb-home-notification-house-scrollbar-width: 3px;
    --tvb-home-notification-house-scrollbar-padding: 5px;

    &.ellipsis {
      --tvb-home-notification-contnet-message-ellipsis-line: 3;
      --tvb-home-notification-height: 163px;
      --tvb-home-notification-contnet-height: 102px;
      --tvb-home-notification-contnet-message-before-height: 56px;
      --tvb-home-notification-contnet-message-ellipsis-display: block;
      --tvb-home-notification-contnet-message-text-overflow: ellipsis;
    }
  }

  position: fixed;
  z-index: 99999;
  right: var(--tvb-home-notification-fixed-offset);
  bottom: var(--tvb-home-notification-fixed-bottom);
  max-width: var(--tvb-home-notification-max-width);
  width: var(--tvb-home-notification-width);
  height: var(--tvb-home-notification-height);
  min-width: calc(320px - 12px * 2);
  background: url(/image/img_message_bg.jpg) no-repeat;
  background-position: center;
  background-size: 100% 100%;
  transition:
    bottom 0.3s linear,
    height 0.15s linear;
  border-radius: var(--tvb-home-notification-border-radius);
  &.show {
    bottom: var(--tvb-home-notification-fixed-offset);
  }

  @media (max-width: 340px) {
    right: unset;
    left: 50%;
    transform: translateX(-50%);
  }
  .tvb-home-notification_container {
    max-width: var(--tvb-home-notification-max-width);
    width: var(--tvb-home-notification-width);
    min-width: calc(320px - 12px * 2);
    height: var(--tvb-home-notification-height);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.6) 0%, #f8f9fa 100%);
    box-shadow: var(--tvb-home-notification-box-shadow);
    border-radius: var(--tvb-home-notification-border-radius);
    backdrop-filter: blur(20px);
    transition: height 0.15s linear;
    .tvb-home-notification_header {
      display: flex;
      align-items: center;
      padding: var(--tvb-home-notification-header-padding);
      user-select: none;
      .horn {
        width: var(--tvb-home-notification-header-horn-width);
        height: var(--tvb-home-notification-header-horn-height);
        .horn-img {
          display: block;
          height: var(--tvb-home-notification-header-horn-size);
          width: var(--tvb-home-notification-header-horn-size);
          margin-bottom: var(--tvb-home-notification-header-horn-margin-bottom);
          position: relative;
          top: var(--tvb-home-notification-header-horn-top);
        }
      }
      .title {
        color: #333333;
        font-size: var(--tvb-home-notification-header-title-font-size);
        font-weight: 500;
        letter-spacing: var(--tvb-home-notification-header-title-letter-spacing);
        line-height: var(--tvb-home-notification-header-title-line-height);
        margin: var(--tvb-home-notification-header-title-margin);
      }
      .number {
        display: block;
        height: var(--tvb-home-notification-header-number-size);
        width: var(--tvb-home-notification-header-number-size);
        background-color: #fea430;
        color: #ffffff;
        font-size: var(--tvb-home-notification-header-number-font-size);
        font-weight: 500;
        letter-spacing: 0;
        line-height: var(--tvb-home-notification-header-number-size);
        text-align: center;
        border-radius: var(--tvb-home-notification-header-number-size);
      }
      .close {
        cursor: pointer;
        height: var(--tvb-home-notification-header-close-box-size);
        width: var(--tvb-home-notification-header-close-box-size);
        position: absolute;
        top: var(--tvb-home-notification-fixed-box-offset);
        right: var(--tvb-home-notification-fixed-box-offset);
        .hover,
        .normal {
          font-size: var(--tvb-home-notification-header-close-size);
          transition: opacity 0.15s linear;
          position: absolute;
          left: calc(
            (
                var(--tvb-home-notification-header-close-box-size) - var(
                    --tvb-home-notification-header-close-size
                  )
              ) / 2
          );
          top: calc(
            (
                var(--tvb-home-notification-header-close-box-size) - var(
                    --tvb-home-notification-header-close-size
                  )
              ) / 2
          );
        }
        .hover {
          opacity: 0;
        }
        &:active {
          opacity: 0.75;
        }
        &:hover {
          .hover {
            opacity: 1;
          }
          .normal {
            opacity: 0;
          }
        }
      }
    }

    .tvb-home-notification_contnet {
      height: var(--tvb-home-notification-contnet-height);
      width: 100%;
      min-width: calc(320px - 12px * 4);
      overflow: hidden;
      transition: height 0.15s linear;
      .tvb-home-notification_swiper {
        width: 100%;
        min-width: calc(320px - 12px * 4);
        height: inherit;
        overflow: unset;
        .tvb-home-notification_swiper-slide {
          max-width: calc(
            var(--tvb-home-notification-max-width) - var(--tvb-home-notification-swiper-offset) * 2
          );
          width: calc(
            var(--tvb-home-notification-width) - var(--tvb-home-notification-swiper-offset) * 2
          );
          min-width: calc(320px - 12px * 4);
          height: var(--tvb-home-notification-contnet-height);
          max-height: var(--tvb-home-notification-contnet-height);
          border: 0.5px solid rgba(163, 178, 188, 0.35);
          border-radius: var(--tvb-home-notification-border-radius);
          background-color: rgba(255, 255, 255, 0.9);
          .content {
            max-width: calc(
              var(--tvb-home-notification-max-width) - var(--tvb-home-notification-swiper-offset) *
                2
            );
            width: calc(
              var(--tvb-home-notification-width) - var(--tvb-home-notification-swiper-offset) * 2 - var(
                  --tvb-home-notification-house-scrollbar-padding
                )
            );
            min-width: calc(320px - 12px * 4);
            max-height: inherit;
            .house {
              max-height: inherit;
              overflow: hidden auto;
              &::-webkit-scrollbar {
                width: var(--tvb-home-notification-house-scrollbar-width);
              }
              &::-webkit-scrollbar-track {
                background: transparent;
              }
              &::-webkit-scrollbar-thumb {
                background: rgba(163, 178, 188, 0.35);
                border-radius: 2px;
              }
            }
            .message {
              padding: var(--tvb-home-notification-contnet-message-padding);
              padding-right: calc(
                var(--tvb-home-notification-contnet-message-padding) - var(
                    --tvb-home-notification-house-scrollbar-padding
                  )
              );
              user-select: none;

              .message-text {
                color: #333333;
                text-align: justify;
                font-size: var(--tvb-home-notification-contnet-message-font-size);
                letter-spacing: var(--tvb-home-notification-contnet-message-letter-spacing);
                line-height: var(--tvb-home-notification-contnet-message-line-height);
                white-space: pre-wrap;

                @supports (-webkit-touch-callout: none) {
                  position: relative;
                  height: calc(
                    var(--tvb-home-notification-contnet-message-line-height) *
                      var(--tvb-home-notification-contnet-message-ellipsis-line)
                  );
                  overflow: hidden;
                  &::after {
                    content: '...';
                    position: absolute;
                    bottom: 0;
                    right: 68px;
                    background-color: transparent;
                    line-height: var(--tvb-home-notification-contnet-message-line-height);
                    display: var(--tvb-home-notification-contnet-message-ellipsis-display, none);
                  }
                  &:not(.unfold)::after {
                    content: '';
                  }
                  &:not(.ellipsis) {
                    height: auto;
                  }
                }

                @supports not (-webkit-touch-callout: none) {
                  @include line-text-ellipsis(
                    var(--tvb-home-notification-contnet-message-ellipsis-line)
                  );
                  text-overflow: var(--tvb-home-notification-contnet-message-text-overflow);
                }

                &::before {
                  content: '';
                  float: right;
                  width: 0;
                  height: var(--tvb-home-notification-contnet-message-before-height);
                }

                .message-unfold {
                  cursor: pointer;
                  float: right;
                  clear: both;
                  color: #0062a3;
                  font-size: 14px;
                  letter-spacing: 0.3px;
                  line-height: 20px;
                  padding: 0;
                  border: 0;
                  background: transparent;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  .message-unfold-arrow {
                    font-size: 12px;
                    opacity: 0.9;
                    transform: scaleX(-1);
                  }
                }
              }
            }
          }
        }
        div[class*='tvb-home-notification_swiper-button'] {
          height: var(--tvb-home-notification-swiper-button-size);
          width: var(--tvb-home-notification-swiper-button-size);
          border: 0.5px solid rgba(163, 178, 188, 0.35);
          background-color: #f8f9fa;
          position: absolute;
          top: calc((100% - var(--tvb-home-notification-swiper-button-size)) / 2);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9;
          border-radius: var(--tvb-home-notification-swiper-button-size);
          cursor: pointer;
          transition: opacity 0.15s linear;
          &.swiper-button-disabled {
            opacity: 0;
            cursor: unset;
            z-index: -1;
          }
          .arrow {
            font-size: var(--tvb-home-notification-swiper-button-arrow-size);
            position: absolute;
            &.hover {
              opacity: 0;
            }
          }
          &:not(.swiper-button-disabled):hover {
            transition:
              border 0.15s linear,
              background-color 0.15s linear;
            border: 0.5px solid rgba(163, 178, 188, 0.35);
            background-color: #ffffff;
            .arrow {
              transition: opacity 0.15s linear;
              &.normal {
                opacity: 0;
              }
              &.hover {
                opacity: 1;
              }
            }
          }
          &:active {
            opacity: 0.75;
          }
          &.tvb-home-notification_swiper-button-prev {
            left: var(--tvb-home-notification-swiper-button-offset);
            .normal {
              transform: rotate(180deg);
            }
          }
          &.tvb-home-notification_swiper-button-next {
            right: var(--tvb-home-notification-swiper-button-offset);
            .hover {
              transform: rotate(180deg);
            }
          }
        }
      }
    }
    .tvb-home-notification_footer {
      .tvb-home-notification_swiper-pagination {
        display: flex;
        align-items: center;
        justify-content: center;
        height: var(--tvb-home-notification-swiper-pagination-height);
        :deep(.swiper-pagination-bullet) {
          height: var(--tvb-home-notification-swiper-pagination-bullet-height);
          width: var(--tvb-home-notification-swiper-pagination-bullet-width);
          cursor: pointer;
          margin: var(--tvb-home-notification-swiper-pagination-bullet-margin);
          border-radius: var(--tvb-home-notification-swiper-pagination-bullet-radius);
          background-color: rgba(163, 178, 188, 0.35);
          transition: background-color 0.15s linear;
          &:first-child {
            margin: 0;
          }
          &.swiper-pagination-bullet-active {
            background-color: #818d95;
          }
        }

        &:not(.mobile) :deep(.swiper-pagination-bullet:hover) {
          background-color: #818d95;
        }
      }
    }
  }
}
</style>
